import { KeyboardEvent, PointerEvent, useEffect, useRef } from 'react';

import { Icon, IconName } from '~/modules/elements/akui/icon';
import { keyStrokes } from '~/modules/remote-mic/network/messages';

type Direction = 'up' | 'right' | 'down' | 'left';

interface Props {
  onPress: (key: keyStrokes) => () => void;
  // The same keystroke without the buzz, for the repeats of a hold - see `keyboard.tsx`.
  onRepeat: (key: keyStrokes) => () => void;
  horizontal: boolean;
  vertical: boolean;
  acceptEnabled: boolean;
}

const DIRECTIONS: Direction[] = ['up', 'right', 'down', 'left'];

// Where each arc sits on the rim. `50 255` of the circle's ~305 circumference is a 59° segment, and
// the rotation turns it onto the arrow that fired it.
const ARC_ROTATION: Record<Direction, number> = { up: -115, right: -25, down: 65, left: 155 };

// Long enough that a deliberate single tap never becomes two, and 8/s once it starts, which is what
// makes a long song list walkable.
const HOLD_DELAY_MS = 400;
const REPEAT_INTERVAL_MS = 125;
const FLASH_MS = 240;
const ARC_MS = 320;
// The hub squashes rather than flashing: it is already filled, so there is no colour to add. Short
// enough to be over before the thumb lifts on anything but a deliberate hold.
const ACCEPT_SQUASH_MS = 180;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// One ring instead of a pad above a row of buttons: the thumb holding the phone reaches its edge
// cells and its hub without moving, and `accept` sitting in the middle is what a remote control has
// always looked like. `accept` and `back` are already separate messages, so putting OK in the hub
// and leaving Back as its own button above the ring is layout and nothing more.
export default function RingController({ onPress, onRepeat, horizontal, vertical, acceptEnabled }: Props) {
  const arcs = useRef(new Map<Direction, SVGCircleElement | null>());
  const acceptArc = useRef<SVGCircleElement | null>(null);

  // Two facts that used to share one signal: the flash says the button took the press, the arc says
  // a keystroke left the phone. Reduced motion keeps the first and drops the second.
  const fire = (direction: Direction) => (repeat: boolean) => {
    if (!reducedMotion()) {
      arcs.current.get(direction)?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: ARC_MS, easing: 'ease-out' });
    }

    (repeat ? onRepeat : onPress)(direction)();
  };

  // The hub's arc is the whole rim rather than a segment of it: OK is not a direction, so there is
  // nowhere on the ring for it to point.
  const fireAccept = () => {
    if (!reducedMotion()) {
      acceptArc.current?.animate([{ opacity: 1 }, { opacity: 0 }], { duration: ARC_MS, easing: 'ease-out' });
    }

    onPress('accept')();
  };

  return (
    <div className="flex min-h-0 flex-1 items-center justify-center pb-1">
      {/* `touch-none`, or a hold that drifts a few pixels scrolls the page instead of repeating. */}
      <div className="relative grid aspect-square max-h-full w-full max-w-[19.375rem] touch-none grid-cols-3 items-center justify-items-center rounded-full bg-black/55 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.16)]">
        {/* Absolute, so the rim arcs do not take a cell of the 3x3 the arrows are laid out on. */}
        <svg viewBox="0 0 100 100" className="pointer-events-none absolute inset-0 size-full overflow-visible">
          {DIRECTIONS.map((direction) => (
            <circle
              key={direction}
              ref={(element) => {
                arcs.current.set(direction, element);
              }}
              cx="50"
              cy="50"
              r="48.5"
              fill="none"
              stroke="rgb(255,165,0)"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeDasharray="50 255"
              transform={`rotate(${ARC_ROTATION[direction]} 50 50)`}
              opacity="0"
              data-test={`arc-${direction}`}
            />
          ))}
          <circle
            ref={acceptArc}
            cx="50"
            cy="50"
            r="48.5"
            fill="none"
            stroke="rgb(255,165,0)"
            strokeWidth="2.5"
            opacity="0"
            data-test="arc-accept"
          />
        </svg>
        <span />
        <Arrow icon="ic:baseline-keyboard-arrow-up" test="arrow-up" onFire={fire('up')} disabled={!vertical} />
        <span />
        <Arrow icon="ic:baseline-keyboard-arrow-left" test="arrow-left" onFire={fire('left')} disabled={!horizontal} />
        <Hub onFire={fireAccept} disabled={!acceptEnabled} />
        <Arrow
          icon="ic:baseline-keyboard-arrow-right"
          test="arrow-right"
          onFire={fire('right')}
          disabled={!horizontal}
        />
        <span />
        <Arrow icon="ic:baseline-keyboard-arrow-down" test="arrow-down" onFire={fire('down')} disabled={!vertical} />
        <span />
      </div>
    </div>
  );
}

// OK, which has no hold and no direction - so it answers a press and does nothing else. Driven from
// the handler rather than from `:active` for the reason the arrows are: touch clears `:active`
// whenever it likes, and a press shorter than a transition never reaches full strength.
function Hub({ onFire, disabled }: { onFire: () => void; disabled: boolean }) {
  const hub = useRef<HTMLButtonElement>(null);

  const fire = () => {
    // The squash survives reduced motion - it is the button answering, not decoration. The rim arc
    // is the decoration, and that is what `fireAccept` drops.
    hub.current?.animate([{ transform: 'scale(0.9)' }, { transform: 'scale(1)' }], {
      duration: ACCEPT_SQUASH_MS,
      easing: 'ease-out',
    });
    onFire();
  };

  // On pointerdown rather than click, so the squash starts when the thumb lands rather than when it
  // lifts. A disabled button suppresses `click` but not `pointerdown`, which is the trap the arrows
  // hit - guarded explicitly rather than left to the attribute.
  const onPointerDown = () => {
    if (!disabled) fire();
  };

  // Firing on pointerdown alone would put the hub out of reach of a keyboard, which the `onClick` it
  // replaced gave for free. `preventDefault` stops the browser synthesising a second press.
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    fire();
  };

  return (
    <button
      ref={hub}
      type="button"
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      disabled={disabled}
      data-test="keyboard-enter"
      className="bg-active flex size-[46%] cursor-pointer items-center justify-center rounded-full border-none text-[1.5rem] font-bold text-black [-webkit-tap-highlight-color:transparent] disabled:bg-gray-500 disabled:text-gray-300">
      OK
    </button>
  );
}

function Arrow({
  icon,
  test,
  onFire,
  disabled,
}: {
  icon: IconName;
  test: string;
  onFire: (repeat: boolean) => void;
  disabled: boolean;
}) {
  const cell = useRef<HTMLButtonElement>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const repeatTimer = useRef<ReturnType<typeof setInterval>>(undefined);

  const endHold = () => {
    clearTimeout(holdTimer.current);
    clearInterval(repeatTimer.current);
  };

  useEffect(() => endHold, []);

  // A screen change can disable an arrow mid-hold, and a disabled button never receives the
  // pointerup that would otherwise have ended the repeat.
  useEffect(() => {
    if (disabled) endHold();
  }, [disabled]);

  const fire = (repeat: boolean) => {
    // Driven from the handler rather than from `:active`, which touch clears at the browser's
    // discretion - a thumb sliding off a round cell could leave it lit. Full strength on the first
    // frame, because with a fade-in the quicker you press the less you see.
    cell.current?.animate([{ backgroundColor: 'rgba(255,165,0,0.9)' }, { backgroundColor: 'rgba(255,165,0,0)' }], {
      duration: FLASH_MS,
      easing: 'ease-out',
    });
    onFire(repeat);
  };

  const startHold = (event: PointerEvent<HTMLButtonElement>) => {
    // A disabled button suppresses `click` but not `pointerdown`, so the arrows the television has
    // not enabled would have started sending keystrokes the moment they stopped firing on click.
    if (disabled) return;

    // Captured, so the hold still ends on this button if the thumb wanders off it.
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // A mouse in a scaled frame can refuse the capture; pointerleave ends the hold instead.
    }

    endHold();
    fire(false);
    holdTimer.current = setTimeout(() => {
      repeatTimer.current = setInterval(() => fire(true), REPEAT_INTERVAL_MS);
    }, HOLD_DELAY_MS);
  };

  // Firing on pointerdown alone would have put the ring out of reach of a keyboard, which is what
  // the `onClick` it replaced gave for free. The browser's own key repeat stands in for the hold.
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    fire(event.repeat);
  };

  return (
    <button
      ref={cell}
      type="button"
      onPointerDown={startHold}
      onPointerUp={endHold}
      onPointerCancel={endHold}
      onPointerLeave={endHold}
      onKeyDown={onKeyDown}
      disabled={disabled}
      data-disabled={disabled}
      data-test={test}
      // Full-bleed so the whole cell of the ring is the target, not just the glyph inside it. The
      // browser's own tap highlight is off, because the flash is the press feedback now.
      className="flex size-full cursor-pointer items-center justify-center rounded-full border-none bg-transparent text-white [-webkit-tap-highlight-color:transparent] disabled:text-white/25">
      <Icon icon={icon} size={8.5} />
    </button>
  );
}
